import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Coins, Crown, Zap, Check, X } from "lucide-react";
import { CharacterCreation } from "@/components/CharacterCreation";
import { AuthModal } from "@/components/AuthModal";
import { BottomNavigation } from "@/components/BottomNavigation";
import { Favorites } from "@/pages/Favorites";
import { MyProfile } from "@/pages/MyProfile";
import { Settings } from "@/pages/Settings";
import { ChatPage } from "@/pages/ChatPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { Explore } from "@/pages/Explore";
import { Premium } from "@/pages/Premium";
import { PrivacySettings } from "@/pages/PrivacySettings";
import { SubscriptionSettings } from "@/pages/SubscriptionSettings";
import { NotificationSettings } from "@/pages/NotificationSettings";
import { ChatList } from "@/pages/ChatList";
import { HelpCenter } from "@/pages/HelpCenter";
import { ContactSupport } from "@/pages/ContactSupport";
import { useToast } from "@/hooks/use-toast";
import { validateUserIdForDB } from "@/lib/uuid-utils";
import { TokenBalanceModal } from "@/components/TokenBalanceModal";
import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";
import { apiClient } from "@/api/ApiClient";
import { useAuth, type AppUser } from "@/contexts/AuthContext";
import { isDebug } from "@/config/env";
import { useTranslation } from "@/hooks/useTranslation";

type AppState = 'character-creation' | 'auth' | 'chat' | 'chat-list' | 'chat-page' | 'my-boyfriends' | 'my-profile' | 'settings' | 'profile-page' | 'explore' | 'premium' | 'privacy-settings' | 'subscription-settings' | 'notification-settings' | 'help-center' | 'contact-support';

interface Character {
  id?: string;
  name: string;
  avatar: string;
  personality: string;
  communicationStyle: string;
  style: string;
  ethnicity: string;
  age: number;
  hairstyle: string;
  bodyType: string;
  clothing: string;
  tattoo: string;
  voice: string;
  interests: string[];
}

const MainApp = () => {
  const { syncLanguageFromServer, t } = useTranslation();
  const { user, setUser, initializing, refreshUserProfile, logout: authLogout } = useAuth();
  const [appState, setAppState] = useState<AppState>('explore');
  const [previousAppState, setPreviousAppState] = useState<AppState>('explore');
  const [character, setCharacter] = useState<Character | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalCharacter, setAuthModalCharacter] = useState<Character | null>(null);
  const [selectedBoyfriendId, setSelectedBoyfriendId] = useState<string>('');
  const [profilePageSource, setProfilePageSource] = useState<'chat' | 'explore' | 'myBoyfriends'>('chat');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [userTokens, setUserTokens] = useState(0);
  const [bottomNavVisible, setBottomNavVisible] = useState(true);
  
  // Navigation state management
  const [exploreState, setExploreState] = useState<{
    currentView: 'grid' | 'profile' | 'chat';
    selectedBoyfriendId?: string;
    fromExplore?: boolean;
  }>({
    currentView: 'grid'
  });

  const [chatState, setChatState] = useState<{
    currentView: 'list' | 'chat' | 'profile';
    selectedBoyfriendId?: string;
    character?: Character | null;
  }>({
    currentView: 'list'
  });

  const [showTokenBalanceModal, setShowTokenBalanceModal] = useState(false);
  const [tokenModalStartAtPayment, setTokenModalStartAtPayment] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState<'monthly' | 'yearly'>('monthly');
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch user tokens
  const fetchUserTokens = async (userId: string) => {
    const userValidation = validateUserIdForDB(userId);
    if (!userValidation.isValid) {
      console.error('Invalid user ID for token fetch:', userValidation.error);
      return 0;
    }
    
    try {
      const data = await apiClient.get(`/user/tokens/${userId}`);
      return data?.tokens || 0;
    } catch (error) {
      console.error('Error fetching user tokens:', error);
      return 0;
    }
  };

  // Fetch fresh user data
  const fetchUserData = async () => {
    try {
      const userData = await apiClient.get('/users/me');
      const mapped = {
        ...userData,
        emailVerified: userData.emailVerified || false,
        notifications_allowed: userData.notificationsAllowed || false,
        isPremium: userData.premium || false,
      };
      if (userData.preferredLanguage) {
        syncLanguageFromServer(userData.preferredLanguage);
      }
      return mapped;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  // Add tokens
  const addTokensToUser = async (tokensToAdd: number) => {
    try {
      const response = await apiClient.addTokens(tokensToAdd);
      
      if (response.success) {
        setUserTokens(response.newTokenCount);
        return true;
      } else {
        console.error('Failed to add tokens:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Error adding tokens:', error);
      return false;
    }
  };

  const getCurrentUserTokens = () => {
    return userTokens;
  };

  // Log debug mode once at startup
  useEffect(() => {
    if (isDebug) {
      try { console.info("[App] Debug mode is ENABLED (VITE_DEBUG_MODE=true)"); } catch {}
    }
  }, []);

  useEffect(() => {
    const handler = async () => {
      if (user?.id) {
        await refreshUserProfile();
        const freshUserData = await fetchUserData();
        if (freshUserData && freshUserData.currentTokens !== undefined) {
          setUserTokens(freshUserData.currentTokens);
        }
      }
    };
    window.addEventListener('tokens-updated', handler as EventListener);
    return () => window.removeEventListener('tokens-updated', handler as EventListener);
  }, [user?.id, refreshUserProfile]);

  useEffect(() => {
    const handler = async () => {
      if (user?.id) {
        await refreshUserProfile();
        const freshUserData = await fetchUserData();
        if (freshUserData) {
          setUser(prev => prev ? { ...prev, ...freshUserData } : null);
          if (freshUserData.currentTokens !== undefined) {
            setUserTokens(freshUserData.currentTokens);
          }
        }
      }
    };
    window.addEventListener('premium-updated', handler as EventListener);
    return () => window.removeEventListener('premium-updated', handler as EventListener);
  }, [user?.id, fetchUserData, refreshUserProfile, setUser]);

  const handleCharacterComplete = (newCharacter: Character) => {
    setCharacter(newCharacter);
    
    if (user) {
      setAppState('chat-page');
    } else {
      setAuthModalCharacter(newCharacter);
      setShowAuthModal(true);
    }
  };

  const handleLogin = () => {
    if (appState === 'premium') {
      sessionStorage.setItem('redirectAfterAuth', 'premium');
    }
    setAuthModalCharacter(null);
    setShowAuthModal(true);
  };

  const handleLogout = async () => {
    if (user && !user.email) {
      const proceed = window.confirm(
        'You have no email set. Logging out in this state will delete your account. Do you want to continue?'
      );
      if (!proceed) {
        return;
      }
    }

    setUserTokens(0);
    setFavorites(new Set());

    await authLogout();

    toast({
      title: "Logged out successfully",
      description: "You have been logged out.",
    });
  };

  const handleAuthComplete = (userData: { id: string; email: string; name?: string }, isSignup?: boolean) => {
    console.log('handleAuthComplete called with:', userData);
    
    const userValidation = validateUserIdForDB(userData.id);
    if (!userValidation.isValid) {
      return;
    }
    
    setShowAuthModal(false);
    
    if (isSignup) {
      navigate(`/verify-email?email=${encodeURIComponent(userData.email)}`);
      return;
    }
    
    setUser(userData);
    
    if (authModalCharacter) {
      const characterWithId = {
        ...authModalCharacter,
        id: authModalCharacter.id || `new-${Date.now()}`
      };
      setCharacter(characterWithId);
      setAppState('chat-page');
    } else {
      const redirectPath = sessionStorage.getItem('redirectAfterAuth');
      if (redirectPath === 'premium') {
        sessionStorage.removeItem('redirectAfterAuth');
        setAppState('premium');
      } else {
        setAppState('explore');
      }
    }
  };

  const handleCloseAuth = () => {
    setShowAuthModal(false);
  };

  const handleNavigateToMyBoyfriends = () => {
    setAppState('my-boyfriends');
  };

  const handleNavigateToProfile = async () => {
    setAppState('my-profile');
    const freshUserData = await fetchUserData();
    if (freshUserData) {
      setUser(prev => prev ? { ...prev, ...freshUserData } : null);
      if (freshUserData.currentTokens !== undefined) {
        setUserTokens(freshUserData.currentTokens);
      }
    }
  };

  const handleCredentialsUpdated = async (email: string) => {
    setUser(prev => (prev ? { ...prev, email } : null));
    const freshUserData = await fetchUserData();
    if (freshUserData) {
      setUser(prev => (prev ? { ...prev, ...freshUserData } : null));
      if (freshUserData.currentTokens !== undefined) {
        setUserTokens(freshUserData.currentTokens);
      }
    }
  };

  const handleNavigateToSettings = () => {
    setAppState('settings');
  };

  const handleNavigateToChat = (boyfriendId?: string) => {
    if (boyfriendId) {
      setSelectedBoyfriendId(boyfriendId);
      setChatState({
        currentView: 'chat',
        selectedBoyfriendId: boyfriendId,
        character: character
      });
    } else {
      setCharacter(null);
      setSelectedBoyfriendId('');
      setChatState({
        currentView: 'list'
      });
    }
    
    if (appState === 'profile-page' && profilePageSource === 'explore') {
      setExploreState({
        currentView: 'chat',
        selectedBoyfriendId: boyfriendId || selectedBoyfriendId,
        fromExplore: true
      });
    }
    
    setAppState('chat-page');
  };

  const handleNavigateToChatList = () => {
    setSelectedBoyfriendId(null);
    setAppState('chat-list');
  };

  const handleNavigateToCharacterCreation = () => {
    setAppState('character-creation');
  };

  const handleSelectBoyfriend = (boyfriend: any) => {
    setCharacter(boyfriend);
    setAppState('chat-page');
  };

  const handleNavigateToProfilePage = (boyfriendId: string) => {
    setSelectedBoyfriendId(boyfriendId);
    setProfilePageSource('chat');
    setChatState({
      currentView: 'profile',
      selectedBoyfriendId: boyfriendId,
      character: character
    });
    setAppState('profile-page');
  };

  const handleBackFromProfilePage = () => {
    if (profilePageSource === 'explore') {
      setExploreState(prev => ({ ...prev, currentView: 'grid' }));
      setAppState('explore');
    } else if (profilePageSource === 'myBoyfriends') {
      setAppState('my-boyfriends');
    } else {
      setAppState('chat-page');
    }
  };

  const handleNavigateToPrivacySettings = () => setAppState('privacy-settings');
  const handleNavigateToSubscriptionSettings = () => setAppState('subscription-settings');
  const handleNavigateToNotificationSettings = () => setAppState('notification-settings');
  const handleNavigateToHelpCenter = () => setAppState('help-center');
  const handleNavigateToContactSupport = () => setAppState('contact-support');
  
  const handleNavigateToExplore = () => {
    setExploreState({ currentView: 'grid' });
    setAppState('explore');
  };

  const handleNavigateToChatButton = () => {
    if (chatState.currentView === 'profile' && chatState.selectedBoyfriendId) {
      setSelectedBoyfriendId(chatState.selectedBoyfriendId);
      setCharacter(chatState.character || null);
      setProfilePageSource('chat');
      setAppState('profile-page');
    } else if (chatState.currentView === 'chat' && chatState.selectedBoyfriendId) {
      setSelectedBoyfriendId(chatState.selectedBoyfriendId);
      setCharacter(chatState.character || null);
      setAppState('chat-page');
    } else {
      setSelectedBoyfriendId('');
      setCharacter(null);
      setAppState('chat-list');
    }
  };

  const handleNavigateToPremium = () => {
    setPreviousAppState(appState);
    setAppState('premium');
  };

  const handleSelectBoyfriendFromExplore = (boyfriendId: string) => {
    setSelectedBoyfriendId(boyfriendId);
    setProfilePageSource('explore');
    setExploreState({
      currentView: 'profile',
      selectedBoyfriendId: boyfriendId,
      fromExplore: true
    });
    setAppState('profile-page');
  };

  const handleNavigateToChatFromExplore = (boyfriendId?: string) => {
    if (boyfriendId) {
      setSelectedBoyfriendId(boyfriendId);
      setExploreState({
        currentView: 'chat',
        selectedBoyfriendId: boyfriendId,
        fromExplore: true
      });
    }
    setAppState('chat-page');
  };

  const handleTokensClick = () => {
    setShowTokenBalanceModal(true);
  };

  const handlePurchaseTokens = async (tokens: number, paymentMethod: string) => {
    // This callback is only called in DEBUG mode from TokenBalanceModal
    // In production, tokens are added via Stripe webhook after successful payment
    if (!isDebug) {
      console.warn('handlePurchaseTokens called in production mode - this should not happen');
      return;
    }
    
    if (user?.id) {
      const userValidation = validateUserIdForDB(user.id);
      if (!userValidation.isValid) {
        toast({
          title: t('tokens.error_title'),
          description: t('tokens.error_debug_purchase'),
          variant: "destructive",
        });
        return;
      }
      
      const success = await addTokensToUser(tokens);
      
      if (success) {
        try {
          if (typeof sessionStorage !== 'undefined' && appState === 'profile-page') {
            sessionStorage.setItem('resumeGenerationAfterTokens', '1');
          }
        } catch {}
        setShowTokenBalanceModal(false);
        toast({
          title: t('tokens.debug_purchase_title'),
          description: t('tokens.debug_purchase_description', { count: tokens }),
        });
        try { window.dispatchEvent(new CustomEvent('tokens-updated')); } catch {}
      } else {
        toast({
          title: t('tokens.error_title'),
          description: t('tokens.error_debug_purchase'),
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: t('tokens.error_title'),
        description: t('tokens.error_sign_in_purchase'),
        variant: "destructive",
      });
    }
  };

  // Bridge: open TokenBalanceModal from global event
  useEffect(() => {
    const handler = () => {
      setTokenModalStartAtPayment(true);
      setShowTokenBalanceModal(true);
    };
    window.addEventListener('open-token-modal', handler as EventListener);
    return () => window.removeEventListener('open-token-modal', handler as EventListener);
  }, []);

  const checkFavoriteStatus = async (boyfriendId: string): Promise<boolean> => {
    try {
      if (!user) return false;
      
      const status = await apiClient.get(`/favorites/${boyfriendId}/status`);
      return status?.isFavorited || false;
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return false;
    }
  };

  const handleToggleFavorite = async (boyfriendId: string) => {
    try {
      if (!user) {
        const isFavorited = favorites.has(boyfriendId);
        
        if (isFavorited) {
          setFavorites(prev => {
            const newSet = new Set(prev);
            newSet.delete(boyfriendId);
            return newSet;
          });
        } else {
          setFavorites(prev => {
            const newSet = new Set([...prev, boyfriendId]);
            return newSet;
          });
        }
        return;
      }

      const isFavorited = favorites.has(boyfriendId);
      
      if (isFavorited) {
        await apiClient.delete(`/favorites/${boyfriendId}`);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(boyfriendId);
          return newSet;
        });
      } else {
        await apiClient.put(`/favorites/${boyfriendId}`);
        setFavorites(prev => {
          const newSet = new Set([...prev, boyfriendId]);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleUpgradeToPremium = async (plan?: 'monthly' | 'yearly') => {
    // This is only used in DEBUG mode
    // In production, premium status is set via Stripe webhook after successful payment
    if (!isDebug) {
      console.warn('handleUpgradeToPremium called in production mode - premium should be set via webhook');
      return;
    }
    
    if (user) {
      setUser(prev => prev ? { ...prev, isPremium: true } : null);
    }
  };

  const handleShowUpgradeModal = (plan: 'monthly' | 'yearly' = 'monthly') => {
    setUpgradePlan(plan);
    setShowUpgradeModal(true);
  };

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: string) => {
    // This callback is only called in DEBUG mode from SubscriptionPaymentModal
    // In production, premium status is set via Stripe webhook after successful payment
    if (!isDebug) {
      console.warn('handlePurchaseSubscription called in production mode - this should not happen');
      return;
    }
    
    handleUpgradeToPremium(plan);
  };

  const handleCancelSubscription = async () => {
    if (user) {
      setUser(prev => prev ? { ...prev, isPremium: false } : null);
    }
  };

  const pageAllowsNav = !['character-creation', 'premium'].includes(appState) && !showPremiumModal;
  const shouldShowNavigation = pageAllowsNav && (appState === 'chat-page' ? bottomNavVisible : true);

  const handleBottomNavVisibilityChange = (visible: boolean) => {
    setBottomNavVisible(visible);
  };

  const getCurrentPage = (): 'explore' | 'chat' | 'myBoyfriends' | 'myProfile' => {
    switch (appState) {
      case 'chat':
      case 'chat-list':
      case 'chat-page':
        return 'chat';
      case 'profile-page':
        if (profilePageSource === 'chat') return 'chat';
        if (profilePageSource === 'myBoyfriends') return 'myBoyfriends';
        if (profilePageSource === 'explore') return 'explore';
        return 'explore';
      case 'my-boyfriends':
        return 'myBoyfriends';
      case 'my-profile':
      case 'contact-support':
      case 'help-center':
      case 'notification-settings':
      case 'privacy-settings':
      case 'subscription-settings':
        return 'myProfile';
      case 'explore':
        return 'explore';
      default:
        return 'explore';
    }
  };

  useEffect(() => {
    if (!initializing && !user) {
      navigate("/");
    }
  }, [initializing, user, navigate]);

  if (initializing || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full">
      <main className="flex-1 overflow-hidden flex flex-col min-h-0">
        {appState === 'character-creation' && (
          <CharacterCreation 
            onComplete={handleCharacterComplete}
            onBack={user ? undefined : () => navigate('/')}
            user={user}
            onNavigateToMyBoyfriends={user ? handleNavigateToMyBoyfriends : undefined}
            onNavigateToProfile={user ? handleNavigateToProfile : undefined}
            onNavigateToSettings={user ? handleNavigateToSettings : undefined}
            onNavigateToCharacterCreation={user ? handleNavigateToCharacterCreation : undefined}
            onNavigateToChat={handleNavigateToChat}
            onLogout={user ? handleLogout : undefined}
            userTokens={userTokens}
          />
        )}
        
        {appState === 'chat-page' && (
          <ChatPage 
            user={user}
            onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToSettings={handleNavigateToSettings}
            onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
            onNavigateToExplore={handleNavigateToExplore}
            onNavigateToPremium={handleNavigateToPremium}
            onLogout={handleLogout}
            onPromptLogin={handleLogin}
            onNavigateToProfilePage={handleNavigateToProfilePage}
            newCharacter={appState === 'chat-page' && character && character.id?.startsWith('new-') ? character : null}
            selectedBoyfriendId={selectedBoyfriendId}
            onTokensClick={handleTokensClick}
            userTokens={userTokens}
            onPurchaseTokens={handlePurchaseTokens}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
            onBottomNavVisibilityChange={handleBottomNavVisibilityChange}
            onBackToList={handleNavigateToChatList}
          />
        )}

        {appState === 'chat-list' && (
          <ChatList 
            user={user}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
            onNavigateToChat={handleNavigateToChat}
            onNavigateToPremium={handleNavigateToPremium}
            onNavigateToExplore={handleNavigateToExplore}
            onPromptLogin={handleLogin}
            onBottomNavVisibilityChange={handleBottomNavVisibilityChange}
          />
        )}
        
        {appState === 'profile-page' && (
          <ProfilePage 
            boyfriendId={selectedBoyfriendId}
            user={user}
            onBack={handleBackFromProfilePage}
            onPromptLogin={handleLogin}
            onNavigateToChat={() => setAppState('chat-page')}
            onNavigateToMyBoyfriends={() => setAppState('my-boyfriends')}
            onNavigateToProfile={() => setAppState('my-profile')}
            onNavigateToSettings={() => setAppState('settings')}
            onNavigateToCharacterCreation={() => setAppState('character-creation')}
            onNavigateToExplore={() => setAppState('explore')}
            onNavigateToPremium={() => setAppState('premium')}
            userTokens={userTokens}
            onUpdateTokens={async (newTokens) => {
              if (!user) {
                toast({
                  title: "Authentication Required",
                  description: "Please sign in to continue",
                  variant: "destructive",
                });
                return;
              }
              
              // If newTokens is the same as current, just update local state (no HTTP call)
              // This happens when backend returns currentTokens after unlock/deduction
              if (newTokens === userTokens) {
                // No change needed
                return;
              }
              
              const tokensToAdd = newTokens - userTokens;
              
              // If tokens were deducted (negative diff), backend already did it - just update local state
              // Only make HTTP call for positive changes (purchases, additions)
              if (tokensToAdd > 0) {
                const success = await addTokensToUser(tokensToAdd);
                if (success) {
                  toast({
                    title: t('tokens.toast_updated_title'),
                    description: t('tokens.toast_updated_description'),
                  });
                  try { window.dispatchEvent(new CustomEvent('tokens-updated')); } catch {}
                } else {
                  toast({
                    title: t('tokens.error_title'),
                    description: t('tokens.toast_update_failed'),
                    variant: "destructive",
                  });
                }
              } else {
                // Negative change - backend already handled it, just update local state
                setUserTokens(newTokens);
                try { window.dispatchEvent(new CustomEvent('tokens-updated')); } catch {}
              }
            }}
            onPurchaseTokens={handlePurchaseTokens}
            source={profilePageSource}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
          />
        )}
        
        {appState === 'my-boyfriends' && (
          <Favorites 
            onSelectBoyfriend={handleSelectBoyfriend}
            onNavigateToChat={handleNavigateToChat}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToSettings={handleNavigateToSettings}
            onCreateNew={handleNavigateToCharacterCreation}
            onNavigateToExplore={handleNavigateToExplore}
            onNavigateToPremium={handleNavigateToPremium}
            onNavigateToBoyfriendProfile={(boyfriendId) => {
              setSelectedBoyfriendId(boyfriendId);
              setProfilePageSource('myBoyfriends');
              setAppState('profile-page');
            }}
            onToggleFavorite={handleToggleFavorite}
            user={user}
            onPromptLogin={handleLogin}
            onTokensClick={handleTokensClick}
            userTokens={userTokens}
          />
        )}
        
        {appState === 'my-profile' && (
          user ? (
            <MyProfile 
              onNavigateToChat={handleNavigateToChat}
              onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
              onNavigateToSettings={handleNavigateToSettings}
              onCreateNew={handleNavigateToCharacterCreation}
              onNavigateToExplore={handleNavigateToExplore}
              onNavigateToPremium={handleNavigateToPremium}
              onNavigateToHelpCenter={handleNavigateToHelpCenter}
              onNavigateToContactSupport={handleNavigateToContactSupport}
              onLogout={handleLogout}
              user={user}
              onTokensClick={handleTokensClick}
              userTokens={userTokens}
              onPurchaseTokens={handlePurchaseTokens}
              onCredentialsUpdated={handleCredentialsUpdated}
            />
          ) : (
            (() => {
              setShowAuthModal(true);
              setAppState('explore');
              return null;
            })()
          )
        )}
        
        {appState === 'settings' && (
          user ? (
            <Settings 
              onNavigateToChat={handleNavigateToChat}
              onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
              onNavigateToProfile={handleNavigateToProfile}
              onCreateNew={handleNavigateToCharacterCreation}
              onNavigateToPrivacySettings={handleNavigateToPrivacySettings}
              onNavigateToSubscriptionSettings={handleNavigateToSubscriptionSettings}
              onNavigateToNotificationSettings={handleNavigateToNotificationSettings}
              onNavigateToExplore={handleNavigateToExplore}
              onNavigateToPremium={handleNavigateToPremium}
              onTokensClick={handleTokensClick}
              onPurchaseTokens={handlePurchaseTokens}
              onUpgradeToPremium={handleUpgradeToPremium}
              user={{
                email: user.email,
                name: user.name,
                isPremium: user.isPremium || false,
                subscriptionTier: user.isPremium ? "Premium" : "Free",
                subscriptionEnd: user.isPremium ? "2024-12-31T23:59:59Z" : undefined
              }}
              userTokens={userTokens}
            />
          ) : (
            (() => {
              setShowAuthModal(true);
              setAppState('explore');
              return null;
            })()
          )
        )}
        
        {appState === 'privacy-settings' && (
          <PrivacySettings onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
        )}
        
        {appState === 'subscription-settings' && (
          <SubscriptionSettings onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onNavigateToExplore={handleNavigateToExplore} onCreateNew={handleNavigateToCharacterCreation} onCancelSubscription={handleCancelSubscription} user={{...user, isPremium: user?.isPremium || false, subscriptionTier: user?.isPremium ? "Premium" : "Free", subscriptionEnd: user?.isPremium ? "2024-12-31T23:59:59Z" : undefined}} />
        )}
        
        {appState === 'notification-settings' && (
          <NotificationSettings onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
        )}
        
        {appState === 'help-center' && (
          <HelpCenter onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
        )}
        
        {appState === 'explore' && (
          <>
            {exploreState.currentView === 'chat' && exploreState.selectedBoyfriendId ? (
              <ChatPage 
                user={user}
                onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
                onNavigateToProfile={handleNavigateToProfile}
                onNavigateToSettings={handleNavigateToSettings}
                onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
                onNavigateToExplore={() => {
                  setExploreState({ currentView: 'grid' });
                  setAppState('explore');
                }}
                onNavigateToPremium={handleNavigateToPremium}
                onLogout={handleLogout}
                onPromptLogin={handleLogin}
                onNavigateToProfilePage={handleNavigateToProfilePage}
                newCharacter={null}
                selectedBoyfriendId={exploreState.selectedBoyfriendId}
                onTokensClick={handleTokensClick}
                userTokens={userTokens}
                onPurchaseTokens={handlePurchaseTokens}
                favorites={favorites}
                onToggleFavorite={handleToggleFavorite}
                onBackToList={() => {
                  setExploreState({ currentView: 'grid' });
                  setAppState('explore');
                }}
              />
            ) : (
              <Explore 
                onNavigateToChat={handleNavigateToChatFromExplore}
                onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
                onNavigateToProfile={handleNavigateToProfile}
                onNavigateToSettings={handleNavigateToSettings}
                onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
                onNavigateToExplore={handleNavigateToExplore}
                onNavigateToPremium={handleNavigateToPremium}
                onSelectBoyfriend={handleSelectBoyfriendFromExplore}
                user={user}
                onPromptLogin={handleLogin}
                onTokensClick={handleTokensClick}
                userTokens={userTokens}
              />
            )}
          </>
        )}
        
        {appState === 'premium' && (
          <Premium 
            onNavigateToChat={handleNavigateToChat}
            onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToSettings={handleNavigateToSettings}
            onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
            onNavigateToExplore={handleNavigateToExplore}
            onNavigateToPremium={handleNavigateToPremium}
            user={user}
            onPromptLogin={handleLogin}
            onTokensClick={handleTokensClick}
            onUpgradeToPremium={() => handleUpgradeToPremium()}
            userTokens={userTokens}
            source={previousAppState === 'chat' ? 'chat' : 'upgrade'}
            onGoBack={() => setAppState(previousAppState)}
          />
        )}
        
        {appState === 'contact-support' && (
          <ContactSupport onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
        )}
      </main>

      {shouldShowNavigation && (
        <BottomNavigation
          currentPage={getCurrentPage()}
          onNavigateToExplore={handleNavigateToExplore}
          onNavigateToChat={handleNavigateToChatButton}
          onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
          onNavigateToProfile={handleNavigateToProfile}
          user={user}
          onPromptLogin={handleLogin}
          unreadMessagesCount={3}
        />
      )}
      
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleCloseAuth}
        onComplete={handleAuthComplete}
        character={authModalCharacter || undefined}
      />
      
      <TokenBalanceModal
        open={showTokenBalanceModal}
        onOpenChange={(open) => {
          setShowTokenBalanceModal(open);
          if (!open) {
            setTokenModalStartAtPayment(false);
            try {
              const shouldResume = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('resumeGenerationAfterTokens') === '1';
              if (shouldResume && appState === 'profile-page') {
                window.dispatchEvent(new CustomEvent('resume-generation'));
              }
            } catch {}
          }
        }}
        userTokens={getCurrentUserTokens()}
        onPurchaseTokens={handlePurchaseTokens}
        startAtPayment={tokenModalStartAtPayment}
      />

      <Dialog open={showPremiumModal} onOpenChange={(open) => {
        setShowPremiumModal(open);
      }}>
        <DialogContent className="w-full h-full max-w-full max-h-full overflow-y-auto relative">
          <DialogHeader>
            <DialogTitle className="text-center text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              Upgrade your romance 💗
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Card className="p-4 border border-pink-400 bg-pink-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Crown className="text-yellow-400" size={24} />
                <div>
                  <h3 className="font-semibold text-lg">Premium Monthly</h3>
                  <p className="text-sm text-muted-foreground">Unlimited chats, priority support, and exclusive content.</p>
                </div>
                <Button
                  variant="default"
                  onClick={() => {
                    setShowPremiumModal(false);
                    handleShowUpgradeModal('monthly');
                  }}
                >
                  Subscribe $9.99/mo
                </Button>
              </div>
            </Card>
            <Card className="p-4 border border-purple-400 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Zap className="text-purple-600" size={24} />
                <div>
                  <h3 className="font-semibold text-lg">Premium Yearly</h3>
                  <p className="text-sm text-muted-foreground">Save 20% with annual billing.</p>
                </div>
                <Button
                  variant="default"
                  onClick={() => {
                    setShowPremiumModal(false);
                    handleShowUpgradeModal('yearly');
                  }}
                >
                  Subscribe $99.99/yr
                </Button>
              </div>
            </Card>
            <Card className="p-4 border border-green-400 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Coins className="text-green-600" size={24} />
                <div>
                  <h3 className="font-semibold text-lg">Buy Tokens</h3>
                  <p className="text-sm text-muted-foreground">Purchase tokens to unlock special features.</p>
                </div>
                <Button
                  variant="default"
                  onClick={() => {
                    setShowPremiumModal(false);
                    setShowTokenBalanceModal(true);
                  }}
                >
                  Buy Tokens
                </Button>
              </div>
            </Card>
            <Card className="p-4 border border-gray-300 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Check className="text-green-500" size={24} />
                <div>
                  <h3 className="font-semibold text-lg">Current Plan</h3>
                  <p className="text-sm text-muted-foreground">{user?.isPremium ? "Premium Subscriber" : "Free User"}</p>
                </div>
              </div>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      <SubscriptionPaymentModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        selectedPlan={upgradePlan}
        onPurchaseSubscription={handlePurchaseSubscription}
      />
    </div>
  );
};

export default MainApp;
